/**
 * @file This custom hook is responsible for fetching and processing document data related to a specific asset.
 * It handles fetching asset relationships, extracting document IDs, and then fetching the corresponding metadata
 * and custom attributes. It also provides functions to clear the document data and manage loading and error states.
 */
import { useState, useEffect } from 'react';
import { FetchAssetRelationShips } from '../api/assetRelationShipsService';
import { docsMetaDataService } from '../api/docsMetaDataService';
import { docsCustomAttributesService } from '../api/docsCustomAttributesService';
import { useAuth } from '../contexts/AuthContext';
import { useGlobalProvider } from '../contexts/GlobalProvider';

/**
 * Custom hook for fetching and processing document data related to a specific asset.
 * @returns {{documentData: Array, loading: boolean, error: Error|null, fetchDocuments: Function, clearDocuments: Function}}
 */
export const useDocumentFetcher = () => {
    const [documentData, setDocumentData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { getAccessToken } = useAuth();
    const { selectedTagId } = useGlobalProvider();

    // Clear document data when selectedTagId changes to prevent showing wrong documents
    useEffect(() => {
        // console.log('selectedTagId changed in useDocumentFetcher, clearing document data');
        setDocumentData([]);
        setError(null);
    }, [selectedTagId]);

    /**
     * Extracts document lineage IDs from a list of asset relationships.
     * @param {Array} relationships - A list of asset relationships.
     * @returns {Array<string>} A list of document lineage IDs.
     */
    const extractDocumentLineageIds = (relationships) => {
        // console.log('relationships:', relationships);
        const ids = [];
        relationships.forEach((relationship) => {
            if (!relationship.isDeleted) {
                relationship.entities.forEach((entity) => {
                    if (entity.domain === 'autodesk-bim360-documentmanagement' && entity.type === 'documentlineage') {
                        ids.push(entity.id);
                    }
                });
            }
        });
        return ids;
    };

    /**
     * Fetches metadata for a list of document IDs.
     * @param {string} selectedProjectId - The ID of the current project.
     * @param {Array<string>} documentIds - A list of document lineage IDs.
     * @param {string} accessToken - The authentication token.
     * @returns {Promise<Array>} A promise that resolves to a list of document metadata.
     */
    const fetchDocumentMetadata = async (selectedProjectId, documentIds, accessToken) => {
        const requests = documentIds.map(id => docsMetaDataService({selectedProjectId, documentIds: id, accessToken}));
        const results = await Promise.all(requests);

        // need to exclude documents still listed by relationships returned by fetchDocumentMetadata with data.attributes.hidden == true
        const filteredResults = results.filter(result => !result.data.attributes.hidden);
        return filteredResults;
    };

    /**
     * Fetches custom attributes for a list of document IDs.
     * @param {string} selectedProjectId - The ID of the current project.
     * @param {Array<string>} documentIds - A list of document lineage IDs.
     * @param {string} accessToken - The authentication token.
     * @returns {Promise<Array>} A promise that resolves to a list of custom attributes.
     */
    const fetchCustomAttributes = async (selectedProjectId, documentIds, accessToken) => {
        const urns = documentIds.map(id => `${id}`);
        const requestPayload = {urns};
        return docsCustomAttributesService({selectedProjectId, requestPayload, accessToken});
    };

    /**
     * Merges document metadata with a default set of attributes when custom attributes are not available.
     * @param {Array} documentMetaData - A list of document metadata.
     * @returns {Array} A list of merged document data.
     */
    const mergeResultsWithoutCustomAttributes = (documentMetaData) => {
        return documentMetaData.map(result => {
            const additionalInfo = { urn: result.data.id }; // Create a basic additionalInfo object
            return {
                itemUrn: additionalInfo.urn, // Use the URN from additionalInfo like in working version
                displayName: result.data.attributes.displayName || result.data.attributes.name || 'Unknown Document',
                name: result.data.attributes.name || 'Unknown',
                fileType: result.data.attributes.fileType || '',
                createTime: result.data.attributes.createTime || '',
                createUserId: result.data.attributes.createUserId || '',
                createUserName: result.data.attributes.createUserName || '',
                lastModifiedTime: result.data.attributes.lastModifiedTime || '',
                lastModifiedUserId: result.data.attributes.lastModifiedUserId || '',
                lastModifiedUserName: result.data.attributes.lastModifiedUserName || '',
                mimeType: result.data.attributes.mimeType || '',
                storageSize: result.data.attributes.storageSize || 0,
                versionNumber: result.data.attributes.versionNumber || 1,
                // Set default values for custom attributes
                tagNumber: '',
                discipline: '',
                documentType: '',
                documentStatus: '',
                documentPhase: ''
            };
        }).filter(item => item !== null);
    };

    /**
     * Merges document metadata with their corresponding custom attributes.
     * @param {Array} documentMetaData - A list of document metadata.
     * @param {Array} customAttributes - A list of custom attributes.
     * @returns {Array} A list of merged document data.
     */
    const mergeResults = (documentMetaData, customAttributes) => {
        const merged = documentMetaData.map(result => {
            const additionalInfo = customAttributes.find(item => item.itemUrn === result.data.id);
            
            if (!additionalInfo) {
                console.warn(`No additional info found for document lineage ID: ${result.data.id}`);
                return null;
            }

            const getCustomAttributeByName = (attributes, attributeName) => {
                if (!attributes) return "";
                const customAttribute = attributes.find(attribute => attribute.name === attributeName);
                return customAttribute ? customAttribute.value : "";
            };

            return {
                itemUrn: additionalInfo.urn, // Use the version URN for the viewer
                description: result.data.attributes.extension.data.description,
                displayName: result.data.attributes.displayName,
                webView: result.data.links.webView.href,
                reference3D: getCustomAttributeByName(additionalInfo.customAttributes, "3 D reference"),
                reference2D: getCustomAttributeByName(additionalInfo.customAttributes, "2 D reference")
            };
        });
        
        return merged.filter(item => item !== null); // Filter out any null results
    };

    /**
     * Fetches all document data for a given asset, including metadata and custom attributes.
     * @param {string} selectedProjectId - The ID of the current project.
     * @param {string} selectedAssetId - The ID of the asset for which to fetch documents.
     */
    const fetchDocuments = async (selectedProjectId, selectedAssetId) => {
        if (!selectedAssetId || selectedAssetId === '') {
            setError('No asset selected');
            return;
        }

        try {
            setLoading(true);
            setError(null);
            const accessToken = await getAccessToken();
            const data = await FetchAssetRelationShips({selectedProjectId, selectedAssetId, accessToken});
            const filteredIds = extractDocumentLineageIds(data.relationships);
            const documentMetaData = await fetchDocumentMetadata(selectedProjectId, filteredIds, accessToken);
            // console.log('documentMetaData:', documentMetaData);
            
            let customAttributes = null;
            try {
                customAttributes = await fetchCustomAttributes(selectedProjectId, filteredIds, accessToken);
                // console.log('customAttributes:', customAttributes);
            } catch (customAttrError) {
                console.warn('Failed to fetch custom attributes, proceeding without them:', customAttrError);
                customAttributes = null;
            }

            if (documentMetaData) {
                const mergedData = customAttributes ?
                    mergeResults(documentMetaData, customAttributes) :
                    mergeResultsWithoutCustomAttributes(documentMetaData);
                setDocumentData(mergedData);
            } else {
                setDocumentData([]);
            }
        } catch (err) {
            setError('No documents for selected asset available');
            setDocumentData([]);
        } finally {
            setLoading(false);
        }
    };

    /**
     * Clears all fetched document data and resets the error state.
     */
    const clearDocuments = () => {
        setDocumentData([]);
        setError(null);
    };

    return {
        documentData,
        loading,
        error,
        fetchDocuments,
        clearDocuments
    };
};